import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, LogOut, Plus } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { adminApi, errorMessage } from "@/lib/api";
import { StatCards } from "@/components/admin/StatCards";
import { InventoryToolbar } from "@/components/admin/InventoryToolbar";
import { InventoryTable } from "@/components/admin/InventoryTable";
import { ProductEditor } from "@/components/admin/ProductEditor";
import { SiteImages } from "@/components/admin/SiteImages";
import { ConfirmButton } from "@/components/admin/ConfirmButton";
import { ChangePassword } from "@/components/admin/ChangePassword";
import { ShopQR } from "@/components/admin/ShopQR";

const DemoBanner = ({ count, onRemove }) => (
  <div className="flex flex-col gap-3 rounded-2xl border border-[#C59B27]/50 bg-[#FBF3DC] p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="demo-data-banner">
    <p className="text-sm text-[#7A5A0B]"><b>{count} sample listings</b> are shown on the website with a "Sample listing" tag. Remove them before launch.</p>
    <ConfirmButton testId="remove-demo-btn" title="Remove all sample listings?" description="All demo products will be deleted. Your real products stay untouched." confirmLabel="Remove samples"
                   onConfirm={onRemove} className="h-10 shrink-0 rounded-full bg-[#8A2B1E] px-5 text-sm font-semibold text-white">Remove sample data</ConfirmButton>
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const [f, setF] = useState({ q: "", category: "", availability: "", published: "", sort: "updated:desc", page: 1 });
  const [q, setQ] = useState("");
  const [editor, setEditor] = useState({ open: false, product: null });
  useEffect(() => { const t = setTimeout(() => setQ(f.q), 300); return () => clearTimeout(t); }, [f.q]);

  const set = (patch) => setF((s) => ({ ...s, ...patch, page: patch.page || 1 }));
  const [sort, order] = f.sort.split(":");
  const stats = useQuery({ queryKey: ["admin", "dashboard"], queryFn: adminApi.dashboard });
  const cats = useQuery({ queryKey: ["admin", "categories"], queryFn: adminApi.categories });
  const params = { q: q || undefined, category: f.category || undefined, availability: f.availability || undefined, published: f.published || undefined, sort, order, page: f.page, page_size: 25 };
  const list = useQuery({ queryKey: ["admin", "products", params], queryFn: () => adminApi.products(params), placeholderData: keepPreviousData });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin"] });

  const onPatch = async (id, body) => { await adminApi.update(id, body); refresh(); };
  const onDelete = async (id) => { await adminApi.remove(id); refresh(); };
  const removeDemo = async () => {
    try { const r = await adminApi.removeDemo(); toast.success(`Removed ${r.deleted} sample listings`); refresh(); } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 border-b border-[#E5E0D8] bg-[#FDFBF7]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div><p className="font-display text-xl font-bold text-[#1B4332]">RamVilas · Owner</p><p className="text-xs text-[#5B625D]" data-testid="admin-user-email">{user?.email}</p></div>
          <div className="flex gap-2">
            <Link to="/" target="_blank" className="hidden sm:inline-flex h-10 items-center gap-2 rounded-full border border-[#E5E0D8] px-4 text-sm font-semibold" data-testid="admin-view-site-link"><ExternalLink className="h-4 w-4" /> View site</Link>
            <ChangePassword />
            <button onClick={logout} className="inline-flex h-10 items-center gap-2 rounded-full border border-[#E5E0D8] px-4 text-sm font-semibold" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6">
        <StatCards stats={stats.data} onFilter={(a) => set({ availability: a })} />
        {stats.data?.demo > 0 && <DemoBanner count={stats.data.demo} onRemove={removeDemo} />}
        <Tabs defaultValue="inventory">
          <TabsList className="h-11 rounded-full bg-[#F4EEE2] p-1">
            <TabsTrigger value="inventory" className="rounded-full px-5" data-testid="admin-tab-inventory">Inventory</TabsTrigger>
            <TabsTrigger value="site" className="rounded-full px-5" data-testid="admin-tab-site">Logo & shop photo</TabsTrigger>
            <TabsTrigger value="qr" className="rounded-full px-5" data-testid="admin-tab-qr">QR code</TabsTrigger>
          </TabsList>
          <TabsContent value="inventory" className="mt-5 space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="font-display text-2xl font-bold text-[#1B4332]">Inventory <span className="text-base font-normal text-[#5B625D]" data-testid="inventory-total">({list.data?.total ?? 0})</span></h2>
              <button onClick={() => setEditor({ open: true, product: null })} className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[#1B4332] px-6 font-semibold text-white" data-testid="add-product-btn"><Plus className="h-4 w-4" /> Add product</button>
            </div>
            <InventoryToolbar f={f} set={set} categories={cats.data?.items} />
            {list.isError && <p className="rounded-xl bg-[#F6E4E1] p-3 text-sm text-[#8A2B1E]" data-testid="inventory-error">{errorMessage(list.error)}</p>}
            <InventoryTable items={list.data?.items || []} loading={list.isPending} onEdit={(p) => setEditor({ open: true, product: p })} onPatch={onPatch} onDelete={onDelete} />
            {list.data?.pages > 1 && (
              <div className="flex items-center justify-center gap-3" data-testid="inventory-pagination">
                <button disabled={f.page <= 1} onClick={() => set({ page: f.page - 1 })} className="h-10 rounded-full border px-4 disabled:opacity-40" data-testid="inventory-prev-page">Previous</button>
                <span className="text-sm">Page {f.page} of {list.data.pages}</span>
                <button disabled={f.page >= list.data.pages} onClick={() => set({ page: f.page + 1 })} className="h-10 rounded-full border px-4 disabled:opacity-40" data-testid="inventory-next-page">Next</button>
              </div>
            )}
          </TabsContent>
          <TabsContent value="site" className="mt-5"><SiteImages /></TabsContent>
          <TabsContent value="qr" className="mt-5"><ShopQR /></TabsContent>
        </Tabs>
      </main>
      <ProductEditor open={editor.open} product={editor.product} categories={cats.data?.items} onClose={() => setEditor({ open: false, product: null })} onSaved={refresh} />
    </div>
  );
}
